// The opening path of a game: the sequence of named positions it passed
// through, with where it left the book. A property of the moves, not of the
// engine, so it needs no scan. Pure functions over positions already
// computed, so the tests feed positions and a fake book.
import { type Color, type Fen, type GameRecord, type GameResult, type GameSource, type GameSummary } from '@chess/api-model';
import { epdOf, type OpeningBook } from '@chess/api-openings';

/** Bumped when the path rule or the book data changes; stored records older than this are remade. */
export const DATA_VERSION = 1;

/** One named position the game passed through. */
export interface PathNode {
  /** Index into the game's positions: plies played to reach it. */
  ply: number;
  eco: string;
  name: string;
  /** The position without clocks: what a node is matched by, so transpositions meet. */
  epd: string;
}

/** The stored record, `history-openings/path.json` in the game's folder. */
export interface OpeningPath {
  schemaVersion: 1;
  dataVersion: number;
  gameId: string;
  path: PathNode[];
  /** Index of the last position the book knows; the move played from it left the book. -1 when the book knows nothing. */
  exitPly: number;
  /** How many moves the game has. */
  moves: number;
  /** Who played the move out of the book: the side to move at `exitPly`. Undefined while the whole game stayed in the book, or when the book knew nothing. */
  leftBy?: Color;
}

/** What the per-player index keeps per game: enough to match and to list a row without opening the folder. */
export interface IndexEntry {
  gameId: string;
  playedAt: string;
  owner: string;
  source: GameSource;
  white: string;
  black: string;
  result: GameResult;
  /** The side the owner played, when the owner is one of the players. */
  reader?: Color;
  /** EPDs of the named positions in the path. */
  nodes: string[];
  exitPly: number;
  moves: number;
}

export interface OpeningIndex {
  schemaVersion: 1;
  dataVersion: number;
  entries: Record<string, IndexEntry>;
}

export function emptyIndex(): OpeningIndex {
  return { schemaVersion: 1, dataVersion: DATA_VERSION, entries: {} };
}

/** The named positions of a game and where it left the book. */
export function pathOf(gameId: string, positions: readonly Fen[], book: OpeningBook): OpeningPath {
  // Every position is looked up, not just the opening prefix, so a game that
  // reaches a named position by another move order is filed under it too.
  const path: PathNode[] = [];
  let exitPly = -1;
  for (let ply = 0; ply < positions.length; ply++) {
    const fen = positions[ply];
    const opening = book.lookup(fen);
    if (opening) path.push({ ply, eco: opening.eco, name: opening.name, epd: epdOf(fen) });
    if (opening || book.knows(fen)) exitPly = ply;
  }
  const moves = Math.max(0, positions.length - 1);
  // White moves from even plies, counting from the standard start.
  const leftBy = exitPly >= 0 && exitPly < moves ? (exitPly % 2 === 0 ? 'white' : 'black') : undefined;
  return { schemaVersion: 1, dataVersion: DATA_VERSION, gameId, path, exitPly, moves, leftBy };
}

/** Which side is the reader's: the review API's one rule, under this plugin's name. */
import { readerSideOf } from '@chess/api-review';
export const readerSide = readerSideOf;

export function entryOf(game: GameSummary | GameRecord, path: OpeningPath): IndexEntry {
  return {
    gameId: game.id,
    playedAt: game.playedAt,
    owner: game.owner,
    source: game.source,
    white: game.white.name,
    black: game.black.name,
    result: game.result,
    reader: readerSide(game),
    nodes: path.path.map((n) => n.epd),
    exitPly: path.exitPly,
    moves: path.moves,
  };
}

/** Whether a game passed through a named position. */
export function passesThrough(entry: IndexEntry, epd: string): boolean {
  return entry.nodes.includes(epd);
}

/** A stored record is usable when it was made by this version of the rule. */
export function isCurrent(value: { schemaVersion?: number; dataVersion?: number } | undefined): boolean {
  return !!value && value.schemaVersion === 1 && value.dataVersion === DATA_VERSION;
}
